import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import * as actions from './actions';
import * as Styled from '../SettingsPage/SettingsPage.styles';
import { selectors } from './reducer';
import CustomToggle from '../../components/CustomToggle';
import { DELIVERY_PROVIDERS, DeliveryProviderName } from './types';

const PROVIDER_LABELS: Record<DeliveryProviderName, string> = {
  yandex: 'Yandex',
  choco: 'Choco',
  wolt: 'Wolt',
};

const SettingsPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const dataConfig = useSelector(selectors.config);
  const dataCardPayment = useSelector(selectors.cardPayment);
  const dataGlovoEnabled = useSelector(selectors.glovoEnabled);
  const dataProviders = useSelector(selectors.deliveryProviders);

  const [config, setConfig] = useState(
    dataConfig?.data.config.delivery_enabled || false
  );
  const [configPromocode, setConfigPromocode] = useState(
    dataConfig?.data.config.promocode_add_enabled || false
  );
  const [version, setVersion] = useState(
    dataConfig?.data.config.min_app_version || ''
  );

  const [cardPayment, setCardPayment] = useState(dataCardPayment || false);

  const [glovoEnabled, setGlovoEnabled] = useState(
    dataGlovoEnabled?.status || false
  );
  const [glovoDisabledDate, setGlovoDisabledDate] = useState<string>('');

  const [providers, setProviders] = useState<
    Record<DeliveryProviderName, boolean>
  >({
    yandex: false,
    choco: false,
    wolt: false,
  });

  useEffect(() => {
    dataConfig?.data && setConfig(dataConfig.data.config.delivery_enabled);
    dataConfig?.data && setVersion(dataConfig.data.config.min_app_version);
    dataConfig?.data &&
      setConfigPromocode(dataConfig.data.config.promocode_add_enabled);
    dataCardPayment && setCardPayment(dataCardPayment);
    dataGlovoEnabled && setGlovoEnabled(dataGlovoEnabled);
  }, [dataConfig, dataCardPayment, dataGlovoEnabled]);

  useEffect(() => {
    if (dataProviders) {
      setProviders((prev) => {
        const next = { ...prev };
        dataProviders.forEach((item) => {
          next[item.provider] = item.enabled;
        });
        return next;
      });
    }
  }, [dataProviders]);

  useEffect(() => {
    dispatch(actions.getConfig.request());
    dispatch(actions.getCardPayment.request());
    dispatch(actions.getDeliveryProviders.request());
    process.env.REACT_APP_GLOVO_EDIT === 'true' &&
      dispatch(actions.getGlovoEnabledStatus.request());
  }, []);

  const isProviderChanged = (provider: DeliveryProviderName) => {
    const serverItem = dataProviders?.find((p) => p.provider === provider);
    return !!serverItem && serverItem.enabled !== providers[provider];
  };

  const providersChanged = DELIVERY_PROVIDERS.some(isProviderChanged);

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVersion(event.target.value);
  };

  const handleClick = () => {
    const isConfigCganges =
      config !== dataConfig?.data.config.delivery_enabled ||
      configPromocode !== dataConfig?.data.config.promocode_add_enabled ||
      version !== dataConfig?.data.config.min_app_version;
    isConfigCganges &&
      dispatch(
        actions.setConfig.request({
          delivery_enabled: config,
          min_app_version: version,
          promocode_add_enabled: configPromocode,
          postEffect: () => {
            dispatch(actions.getConfig.request());
          },
        })
      );

    if (cardPayment !== dataCardPayment) {
      dispatch(
        actions.setCardPayment.request({
          enabled: cardPayment,
          postEffect: () => {
            dispatch(actions.getCardPayment.request());
          },
        })
      );
    }

    DELIVERY_PROVIDERS.forEach((provider) => {
      if (isProviderChanged(provider)) {
        dispatch(
          actions.setDeliveryProviderEnabled.request({
            provider,
            enabled: providers[provider],
            postEffect: () => {
              dispatch(actions.getDeliveryProviders.request());
            },
          })
        );
      }
    });

    if (glovoEnabled?.status !== dataGlovoEnabled?.status) {
      const action: keyof typeof actions = glovoEnabled?.status
        ? 'setEnambleGlovo'
        : 'setDisableGlovo';

      const until = glovoEnabled?.status
        ? {}
        : { until: new Date(glovoDisabledDate).toISOString() };

      dispatch(
        actions[action].request({
          ...until,
          postEffect: () => {
            setGlovoDisabledDate('');
            dispatch(actions.getGlovoEnabledStatus.request());
          },
        })
      );
    }
  };

  const onClickWolt = () => {
    dispatch(
      actions.setWoltEnabled.request({
        postEffect: (href: string) => {
          window.open(href, '_blank', 'noopener,noreferrer');
        },
      })
    );
  };

  return (
    <Styled.SettingsPageContainer>
      <div className="container">
        <div className="title">{t('SettingsPage.TITLE')}</div>
        <div className="toggle-payment">
          <CustomToggle
            checked={config}
            onChange={() => setConfig(!config)}
            title={t('SettingsPage.ON_SETTINGS')}
            id="paid"
            variant="standart"
          />
        </div>
      </div>

      <div className="container">
        <div className="title">{t('SettingsPage.TITLE-PROMO_CODE')}</div>
        <div className="toggle-payment">
          <CustomToggle
            checked={configPromocode}
            onChange={() => setConfigPromocode(!configPromocode)}
            title={t('SettingsPage.ON_SETTINGS')}
            id="promocode"
            variant="standart"
          />
        </div>
      </div>
      <div className="container">
        <div className="title">{t('OrderPage.StatusOperator.VERSION')}</div>
        <input type="text" value={version} onChange={handleChangeInput} />
      </div>
      <div className="container">
        <div className="title">{t('SettingsPage.TITLE-CARD_PAYMENT')}</div>
        <div className="toggle-card-payment">
          <CustomToggle
            checked={cardPayment}
            onChange={() => setCardPayment(!cardPayment)}
            title={t('SettingsPage.ON_SETTINGS')}
            id="cardPayment"
            variant="standart"
          />
        </div>
      </div>

      <div className="container provider-container">
        <div className="title">
          {t('SettingsPage.TITLE-DELIVERY_PROVIDERS')}
        </div>
        {DELIVERY_PROVIDERS.map((provider) => (
          <div key={provider} className="toggle-card-payment">
            <div>{PROVIDER_LABELS[provider]}</div>
            <div className="toggle-status">
              <CustomToggle
                checked={providers[provider]}
                onChange={() =>
                  setProviders((prev) => ({
                    ...prev,
                    [provider]: !prev[provider],
                  }))
                }
                title={t('SettingsPage.ON_SETTINGS')}
                id={`delivery-provider-${provider}`}
                variant="standart"
              />
            </div>
          </div>
        ))}
      </div>

      {process.env.REACT_APP_GLOVO_EDIT === 'true' && (
        <div className="container">
          <div className="title">{t('SettingsPage.TITLE-GLOVO')}</div>
          <div className="toggle-card-payment">
            <CustomToggle
              checked={glovoEnabled?.status}
              onChange={() =>
                setGlovoEnabled({
                  ...glovoEnabled,
                  status: !glovoEnabled?.status,
                })
              }
              title={t('SettingsPage.ON_SETTINGS')}
              id="glovo"
              variant="standart"
            />
          </div>
          {!glovoEnabled?.status &&
            !!dataGlovoEnabled &&
            glovoEnabled?.status !== dataGlovoEnabled?.status && (
              <>
                <div>{t('SettingsPage.CLOSE_UNTIL')}</div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Отключить до"
                    value={glovoDisabledDate}
                    onChange={(v) => setGlovoDisabledDate(String(v))}
                    ampm={false}
                    inputFormat="DD.MM.YYYY HH:mm"
                    renderInput={(params) => <TextField {...params} />}
                    disablePast
                  />
                </LocalizationProvider>
              </>
            )}
          {!glovoEnabled.status &&
            !!dataGlovoEnabled &&
            glovoEnabled.status === dataGlovoEnabled?.status &&
            dataGlovoEnabled?.until && (
              <div>
                {t('SettingsPage.CLOSED_UNTIL')}
                {new Date(dataGlovoEnabled?.until).toLocaleString() || ''}
              </div>
            )}
        </div>
      )}

      <div className="container">
        <div className="title">{t('SettingsPage.WOLT_ADDING_PHARMACY')}</div>

        <div className="wolt-btn" onClick={() => onClickWolt()}>
          +
        </div>
      </div>
      <div
        className={`confirm-btn ${
          config === dataConfig?.data.config.delivery_enabled &&
          configPromocode === dataConfig?.data.config.promocode_add_enabled &&
          version === dataConfig?.data.config.min_app_version &&
          cardPayment === dataCardPayment &&
          !providersChanged &&
          (glovoEnabled?.status === dataGlovoEnabled?.status ||
            (!glovoEnabled?.status && !glovoDisabledDate)) &&
          'disabled'
        }`}
        onClick={() =>
          (config !== dataConfig?.data.config.delivery_enabled ||
            configPromocode !== dataConfig?.data.config.promocode_add_enabled ||
            version !== dataConfig?.data.config.min_app_version ||
            cardPayment !== dataCardPayment ||
            providersChanged ||
            glovoEnabled?.status !== dataGlovoEnabled?.status) &&
          handleClick()
        }
      >
        {t('OrderPage.StatusOperator.SAVE')}
      </div>
    </Styled.SettingsPageContainer>
  );
};

export default SettingsPage;
